import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Briefcase, Search, MapPin, Calendar, User, Plus, Home, MessageSquare, AlertCircle, Wrench, HelpCircle, Bell } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../App';
import Footer from '../components/Footer';

const AnunciosPage = ({ user }) => {
  const navigate = useNavigate();
  const [anuncios, setAnuncios] = useState([]);
  const [loading, setLoading] = useState(true);
  const [category, setCategory] = useState('all');
  const [searchLocation, setSearchLocation] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);

  const categories = [
    { value: 'all', label: 'Todas las categorías', icon: null },
    { value: 'ofrecer-servicios', label: 'Ofrezco servicios', icon: Wrench },
    { value: 'buscar-servicios', label: 'Busco servicios', icon: HelpCircle },
    { value: 'otros', label: 'Otros', icon: MessageSquare }
  ];

  useEffect(() => {
    fetchAnuncios();
    if (user) {
      fetchUnreadCount();
    }
  }, [category, user]);

  const fetchUnreadCount = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/my-messages/unread-count`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUnreadCount(data.unread_count);
      }
    } catch (error) {
      console.error('Error fetching unread count:', error);
    }
  };

  const fetchAnuncios = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (category !== 'all') params.append('category', category);
      if (searchLocation) params.append('location', searchLocation);
      
      const response = await fetch(`${API}/anuncios?${params.toString()}`);
      if (response.ok) {
        const data = await response.json();
        setAnuncios(data);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Error al cargar anuncios');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchAnuncios();
  };

  const getCategoryBadge = (cat) => {
    const colors = {
      'ofrecer-servicios': 'bg-green-100 text-green-700',
      'buscar-servicios': 'bg-blue-100 text-blue-700',
      'otros': 'bg-gray-100 text-gray-700'
    };
    const labels = {
      'ofrecer-servicios': 'Ofrezco',
      'buscar-servicios': 'Busco',
      'otros': 'Otros'
    };
    return <Badge className={colors[cat] || 'bg-gray-100'}>{labels[cat] || cat}</Badge>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });
  };

  const daysRemaining = (expiresAt) => {
    const now = new Date();
    const expires = new Date(expiresAt);
    const diff = Math.ceil((expires - now) / (1000 * 60 * 60 * 24));
    return diff;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Navigation */}
      <nav className="bg-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center space-x-3">
              <Briefcase className="w-7 h-7 text-red-600" />
              <span className="text-xl font-bold bg-gradient-to-r from-red-600 to-red-700 bg-clip-text text-transparent">
                JobSuiza
              </span>
            </Link>

            <div className="flex items-center space-x-2 sm:space-x-4">
              <Link to="/">
                <Button variant="ghost" size="sm">
                  <Home className="w-4 h-4 sm:mr-2" />
                  <span className="hidden sm:inline">Inicio</span>
                </Button>
              </Link>
              {user ? (
                <>
                  <Link to="/mis-anuncios" className="relative">
                    <Button variant="ghost" size="sm">
                      <Bell className="w-4 h-4 sm:mr-2" />
                      <span className="hidden sm:inline">Mis Anuncios</span>
                      {unreadCount > 0 && (
                        <span className="absolute -top-1 -right-1 bg-red-600 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                          {unreadCount > 9 ? '9+' : unreadCount}
                        </span>
                      )}
                    </Button>
                  </Link>
                  <Link to="/publicar-anuncio">
                    <Button className="bg-red-600 hover:bg-red-700" size="sm">
                      <Plus className="w-4 h-4 mr-1" />
                      Publicar
                    </Button>
                  </Link>
                </>
              ) : (
                <Link to="/auth">
                  <Button variant="outline" size="sm">Entrar</Button>
                </Link>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Header */}
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 text-white py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h1 className="text-4xl font-bold mb-4">Tablón de Anuncios</h1>
          <p className="text-xl text-slate-300">Servicios y anuncios de la comunidad hispanohablante en Suiza</p>
        </div>
      </div>

      {/* Rules Section */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4 sm:p-6">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-6 h-6 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-amber-800 mb-2">📋 Normas del Tablón</h3>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• <strong>Publicar es GRATIS</strong>. No cobramos nada por usar el tablón.</li>
                  <li>• <strong>Solo usuarios registrados</strong> pueden publicar anuncios.</li>
                  <li>• Todos los anuncios son <strong>revisados por moderadores</strong> antes de publicarse.</li>
                  <li>• Los anuncios tienen una <strong>duración máxima de 30 días</strong>.</li>
                  <li>• <strong>No se muestran datos personales</strong> (email/teléfono). El contacto es privado a través de la plataforma.</li>
                  <li>• <strong>Los mensajes se reciben en "Mis Anuncios"</strong>. Revisa tu cuenta periódicamente para ver las respuestas.</li>
                  <li>• Está prohibido publicar contenido ilegal, ofensivo o spam.</li>
                  <li>• JobSuiza no se responsabiliza de las transacciones entre usuarios.</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <Button
                key={cat.value}
                variant={category === cat.value ? "default" : "outline"}
                onClick={() => setCategory(cat.value)}
                className={category === cat.value ? "bg-red-600 hover:bg-red-700" : ""}
                size="sm"
              >
                {cat.icon && <cat.icon className="w-4 h-4 mr-1" />}
                {cat.label}
              </Button>
            ))}
          </div>
          
          <form onSubmit={handleSearch} className="flex gap-2 flex-1 max-w-md">
            <Input
              placeholder="Buscar por ubicación..."
              value={searchLocation}
              onChange={(e) => setSearchLocation(e.target.value)}
              className="flex-1"
            />
            <Button type="submit" variant="outline">
              <Search className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>

      {/* Anuncios Grid */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {loading ? (
          <div className="text-center py-12">
            <div className="w-12 h-12 border-4 border-red-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-slate-600">Cargando anuncios...</p>
          </div>
        ) : anuncios.length === 0 ? (
          <div className="text-center py-12">
            <MessageSquare className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-slate-700 mb-2">No hay anuncios</h3>
            <p className="text-slate-500 mb-4">
              {category !== 'all' ? 'No hay anuncios en esta categoría.' : 'Sé el primero en publicar un anuncio.'}
            </p>
            {user ? (
              <Link to="/publicar-anuncio">
                <Button className="bg-red-600 hover:bg-red-700">
                  <Plus className="w-4 h-4 mr-2" />
                  Publicar Anuncio
                </Button>
              </Link>
            ) : (
              <Link to="/auth">
                <Button variant="outline">Inicia sesión para publicar</Button>
              </Link>
            )}
          </div>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {anuncios.map((anuncio) => (
              <Card 
                key={anuncio.id} 
                className="hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => navigate(`/anuncios/${anuncio.id}`)}
              >
                <CardContent className="p-5">
                  <div className="flex justify-between items-start mb-3">
                    {getCategoryBadge(anuncio.category)}
                    <span className="text-xs text-slate-500">
                      {daysRemaining(anuncio.expires_at) > 0 
                        ? `${daysRemaining(anuncio.expires_at)} días restantes`
                        : 'Expira hoy'}
                    </span>
                  </div>
                  
                  <h3 className="font-semibold text-lg text-slate-900 mb-2 line-clamp-2">
                    {anuncio.title}
                  </h3>
                  
                  <p className="text-slate-600 text-sm mb-4 line-clamp-3">
                    {anuncio.description}
                  </p>
                  
                  <div className="flex items-center justify-between text-sm text-slate-500">
                    <div className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {anuncio.location}
                    </div>
                    <div className="flex items-center gap-1">
                      <Calendar className="w-4 h-4" />
                      {formatDate(anuncio.created_at)}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* CTA for non-logged users */}
      {!user && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <Card className="bg-gradient-to-r from-red-600 to-red-700 text-white">
            <CardContent className="p-8 text-center">
              <h3 className="text-2xl font-bold mb-2">¿Tienes algo que ofrecer o necesitas un servicio?</h3>
              <p className="text-red-100 mb-4">Regístrate gratis y publica tu anuncio</p>
              <Link to="/auth">
                <Button variant="secondary" size="lg">
                  Crear cuenta gratis
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AnunciosPage;
